import React from "react";

import {
  AccountCircleRounded,
  ArrowBackIosNewRounded,
  CancelScheduleSendRounded,
  CommentRounded,
  CommentsDisabledRounded,
  MoreVertRounded,
  RemoveRedEyeRounded,
  SendRounded,
  VisibilityOffRounded,
} from "@mui/icons-material";
import { Box, Divider, ListItemIcon, Menu, MenuItem } from "@mui/material";
import { serverTimestamp } from "firebase/firestore";
import { DateTime } from "luxon";
import { useNavigate, useParams } from "react-router-dom";

import useAuth from "../../config/context/authContext";
import useModal from "../../config/context/modalContext";
import { ChatService } from "../../config/services/ChatService";
import { Colors, GreyPallet } from "../../config/styles";
import { Dev } from "../../config/util/consoleUtil";
import { UtilComponents } from "../../config/util/utilComponents";

import TextMain from "../../components/InputMain/TextMain";
import MainBorderButton from "../../components/MainBorderButton";
import MainSubmit from "../../components/MainSubmit";
import MainText from "../../components/MainText";
import PageHeader from "../../components/PageHeader";
import LoadingPage from "../LoadingPage";

import sendSound from "../../assets/sounds/message-1.mp3";
import receiveSound from "../../assets/sounds/message-2.mp3";

export default function AccountChatPage() {
  const params = useParams();

  const navigate = useNavigate();

  const { currentUserState, currentUserInfo } = useAuth();

  const { pushAlert } = useModal();

  const ChatLazyList = ChatService.LazyList();
  const ChatSubscribe = ChatService.Subscribe();
  const ChatSend = ChatService.Send();
  const ChatOne = ChatService.One();
  const ChatUpdateLastRead = ChatService.Update();
  const ChatUpdate = ChatService.Update();

  const sendAudioObject = new Audio(sendSound);
  const receiveAudioObject = new Audio(receiveSound);

  const ScrollBoxRef = React.useRef(null);

  const [textField, setTextField] = React.useState("");

  const [lastReadMessage, setLastReadMessage] = React.useState("");

  const [shadow, setShadow] = React.useState<"top" | "bottom" | "both">();

  const [anchorMenu, setAnchorMenu] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorMenu);

  const isMessageDisabled = React.useMemo(
    () => !UtilComponents.isStaff(currentUserState) && !ChatOne?.item?.enabled,
    [currentUserState, ChatOne?.item?.enabled]
  );

  const handleClickMenu = (event?: React.MouseEvent<HTMLButtonElement>) => {
    if (event) setAnchorMenu(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorMenu(null);
  };

  const handleSendMessage = async () => {
    const dateNow = new Date();
    const textToSend = textField;
    setTextField("");

    await ChatSend.runRequest(String(params?.chatId), {
      content: textToSend,
      createdAtTimestamp: dateNow.getTime(),
      createdAtISO: dateNow.toISOString(),
      serverTimestamp: serverTimestamp(),
      userId: currentUserState?.uid,
      type: "text",
    }).catch((err) => {
      console.log(...new Dev("handleSendMessage", err).Error());
      pushAlert({
        type: "error",
        message: "Erro ao enviar. " + (err as any).message,
      });
    });
  };

  const handleUpdateChatSettings = async (payload: { visible?: boolean; enabled?: boolean }) => {
    await ChatUpdate.runRequest(String(params?.chatId), payload)
      .catch((err) => {
        console.log(...new Dev("handleUpdateChatSettings", err).Error());
        pushAlert({
          type: "error",
          message: "Erro ao atualizar. " + (err as any).message,
        });
      })
      .finally(() => handleFetchData());
  };

  const handleUpdateLastRead = async (messageId: String) => {
    console.log(...new Dev("handleUpdateLastRead Updating", messageId).Info());

    const lastReadKey = `usersLastRead.${currentUserState?.uid}`;
    const usersLastRead = {} as any;
    usersLastRead[lastReadKey] = messageId;

    await ChatUpdateLastRead.runRequest(String(params?.chatId), usersLastRead).catch((err) => {
      console.log(...new Dev("handleUpdateLastRead", err).Error());
      pushAlert({
        type: "error",
        message: "Erro ao atualizar última mensagem lida. " + (err as any).message,
      });
    });
  };

  const handleLoadPrevious = () => {
    if (currentUserState && params?.chatId) {
      ChatLazyList.runRequest(params?.chatId).catch((err) => {
        console.log(...new Dev("handleLoadPrevious", err).Error());
        pushAlert({
          type: "error",
          message: "Erro ao carregar mensagens. " + (err as any).message,
        });
      });
    }
  };

  const handleSubscribe = () => {
    if (currentUserState && params?.chatId) {
      ChatSubscribe.runRequest(params?.chatId, ChatLazyList?.firstItemRef).catch((err) => {
        console.log(...new Dev("handleSubscribe", err).Error());
        pushAlert({
          type: "error",
          message: "Erro ao obter mensagens. " + (err as any).message,
        });
      });
    }
  };

  const handleFetchData = async () => {
    if (currentUserState && params?.chatId) {
      await ChatOne.runRequest(String(params?.chatId)).catch((err) => {
        console.log(...new Dev("handleFetchData", err).Error());
        pushAlert({
          type: "error",
          message: "Erro ao obter detalhes da conversa. " + (err as any).message,
        });
      });
    }
  };

  const handleScrollTo = () => {
    if (ScrollBoxRef.current) {
      (ScrollBoxRef?.current as any)?.scrollTo({
        top: parseInt((ScrollBoxRef?.current as any)?.scrollHeight),
        behavior: "smooth",
      });
    }
  };

  const handleScroll = (event?: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const target: any = event?.target || ScrollBoxRef.current;
    if (target.scrollHeight > target.offsetHeight) {
      if (target.scrollTop < 50) {
        setShadow("bottom");
      } else if (target.scrollTop > target.scrollHeight - 50 - target.offsetHeight) {
        setShadow("top");
      } else {
        setShadow("both");
      }
    }
  };

  React.useEffect(() => {
    if (!params?.chatId) navigate("/");

    handleFetchData();
    handleLoadPrevious();

    return () => {
      console.log(...new Dev("Chat Unsub", (window as any).unsubSnaps).Purple());
      if ((window as any).unsubSnaps) (window as any).unsubSnaps.map((item: any) => item());
      delete (window as any).unsubSnaps;
    };
  }, []);

  React.useEffect(() => {
    if (ChatOne.item?.usersLastRead?.[String(currentUserState?.uid)])
      setLastReadMessage(String(ChatOne.item?.usersLastRead?.[String(currentUserState?.uid)]));
  }, [ChatOne.item]);

  React.useEffect(() => {
    if (
      ChatLazyList?.items[0]?.userId !== currentUserState?.uid &&
      ChatSubscribe?.items?.length === 0 &&
      lastReadMessage !== ChatLazyList?.items[0]?.id &&
      ChatLazyList?.items[0]?.id
    )
      handleUpdateLastRead(ChatLazyList?.items[0]?.id);

    if (ChatLazyList.items?.length && ChatSubscribe.items.length === 0) handleSubscribe();
    handleScroll();
  }, [ChatLazyList.items]);

  React.useEffect(() => {
    if (ChatSubscribe.items?.length) {
      handleScroll();
      handleScrollTo();

      if (
        ChatSubscribe?.items[ChatSubscribe.items.length - 1]?.userId !== currentUserState?.uid &&
        lastReadMessage !== ChatSubscribe?.items[ChatSubscribe?.items?.length - 1].id
      )
        handleUpdateLastRead(ChatSubscribe.items[ChatSubscribe?.items?.length - 1].id);

      if (ChatSubscribe.items[ChatSubscribe.items?.length - 1].userId === currentUserState?.uid) {
        sendAudioObject?.play();
      } else {
        receiveAudioObject?.play();
      }
    }
  }, [ChatSubscribe.items]);

  return ChatOne.isLoading || ChatSubscribe.isLoading ? (
    <LoadingPage />
  ) : (
    <Box
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        height: "95vh",
        boxSizing: "border-box",
      }}
      sx={{ "& *": { boxSizing: "border-box" } }}
      pt={"30px"}
      pb={"45px"}
    >
      <Box width={"90%"} height={"100%"} position={"relative"}>
        <PageHeader
          title={
            ChatOne?.item?.isStaff && !UtilComponents.isStaff(currentUserState)
              ? "Desenvolvedor"
              : ChatOne?.item?.usersDetails
                  .filter((item) => item.userId !== currentUserState?.uid)
                  .map((item) => item.name)
                  .join(", ") || ""
          }
          leftIcon={<ArrowBackIosNewRounded />}
          onLeftAction={() => navigate(-1)}
          rightIcon={UtilComponents.isStaff(currentUserState) ? <MoreVertRounded /> : undefined}
          onRightAction={UtilComponents.isStaff(currentUserState) ? handleClickMenu : undefined}
        />

        <Menu
          id="basic-menu"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorEl={anchorMenu}
          open={openMenu}
          onClose={handleCloseMenu}
          MenuListProps={{ "aria-labelledby": "basic-button" }}
          sx={{ maxWidth: "80vw" }}
        >
          <Box sx={{ minWidth: "40vw", maxWidth: "60vw" }}>
            <MenuItem
              onClick={() => {
                handleCloseMenu();
                handleUpdateChatSettings({ enabled: !ChatOne?.item?.enabled });
              }}
            >
              <ListItemIcon>
                {ChatOne?.item?.enabled ? (
                  <CommentsDisabledRounded fontSize="small" />
                ) : (
                  <CommentRounded fontSize="small" />
                )}
              </ListItemIcon>
              <MainText canOverflow>
                {ChatOne?.item?.enabled ? "Proibir Mensagens" : "Permitir Mensagens"}
              </MainText>
            </MenuItem>

            <MenuItem
              onClick={() => {
                handleCloseMenu();
                handleUpdateChatSettings({ visible: !ChatOne?.item?.visible });
              }}
            >
              <ListItemIcon>
                {ChatOne?.item?.visible ? (
                  <VisibilityOffRounded fontSize="small" />
                ) : (
                  <RemoveRedEyeRounded fontSize="small" />
                )}
              </ListItemIcon>
              <MainText canOverflow>
                {ChatOne?.item?.visible ? "Esconder Chat" : "Mostrar Chat"}
              </MainText>
            </MenuItem>

            <Divider />

            {ChatOne?.item?.usersDetails.map((item) => (
              <MenuItem
                key={item.userId}
                onClick={() => {
                  navigate("/user/" + item.userId);
                  handleCloseMenu();
                }}
              >
                <ListItemIcon>
                  <AccountCircleRounded fontSize="small" />
                </ListItemIcon>
                <Box width={"100%"} overflow={"hidden"}>
                  <MainText canOverflow>{item.name || "-"}</MainText>
                  <MainText size="tiny" color="regular" inline canOverflow>
                    {item.email}
                  </MainText>
                </Box>
              </MenuItem>
            ))}
          </Box>
        </Menu>

        <Box
          sx={{
            position: "relative",
            borderRadius: "15px",
            maxHeight: "calc(100% - 100px)",
            height: "100%",
            overflow: "hidden",
            border: `3px solid ${GreyPallet.LightGhost}`,
            mb: 2,
          }}
        >
          <div
            style={{
              transition: "200ms",
              opacity: shadow === "top" || shadow === "both" ? 1 : 0,
              position: "absolute",
              width: "100%",
              backgroundImage: "linear-gradient(rgba(0,0,0,0.2), transparent)",
              height: 50,
            }}
          />

          <div
            style={{
              transition: "200ms",
              opacity: shadow === "bottom" || shadow === "both" ? 1 : 0,
              position: "absolute",
              bottom: 0,
              width: "100%",
              backgroundImage: "linear-gradient(transparent, rgba(0,0,0,0.2))",
              height: 50,
            }}
          />

          <Box
            ref={ScrollBoxRef}
            onScroll={handleScroll}
            sx={{
              height: "100%",
              p: 1,
              overflowX: "hidden",
              overflowY: "auto",
              boxSizing: "border-box",
              transition: "200ms",
            }}
          >
            {ChatLazyList?.hasMore ? (
              <Box mb={2}>
                <MainBorderButton
                  onClick={handleLoadPrevious}
                  loading={ChatLazyList.isLoading}
                  text="Carregar Mais"
                />
              </Box>
            ) : null}

            {ChatLazyList?.items
              ? ChatLazyList?.items
                  .map((item) => (
                    <Box
                      key={item.id}
                      width={"100%"}
                      display={"flex"}
                      mb={1}
                      justifyContent={item.userId === currentUserState?.uid ? "end" : "start"}
                      textAlign={item.userId === currentUserState?.uid ? "right" : "left"}
                    >
                      <Box
                        py={1}
                        px={2}
                        sx={{
                          width: item.type === "system" ? "100%" : "auto",
                          maxWidth: item.type === "system" ? "100%" : "80%",
                          backgroundColor:
                            item.type === "system" ? Colors.CetSecondary : GreyPallet.LightGhost,
                          border:
                            item.userId === currentUserState?.uid
                              ? undefined
                              : `3px solid ${GreyPallet.LightGhost}`,
                          borderRadius: "15px",
                        }}
                      >
                        <MainText size="body" color={item.type === "system" ? "white" : "black"}>
                          {item.content}
                        </MainText>
                        <MainText size="small" color="regular">
                          {DateTime.fromMillis(item.createdAtTimestamp).toFormat("dd/LL/y HH:mm")}
                        </MainText>
                      </Box>
                    </Box>
                  ))
                  .reverse()
              : null}

            {ChatSubscribe?.items
              ? ChatSubscribe?.items.map((item) => (
                  <Box
                    key={item.id}
                    width={"100%"}
                    display={"flex"}
                    mb={1}
                    justifyContent={item.userId === currentUserState?.uid ? "end" : "start"}
                    textAlign={item.userId === currentUserState?.uid ? "right" : "left"}
                  >
                    <Box
                      py={1}
                      px={2}
                      sx={{
                        maxWidth: "80%",
                        backgroundColor: GreyPallet.LightGhost,
                        border:
                          item.userId === currentUserState?.uid
                            ? undefined
                            : `3px solid ${GreyPallet.LightGhost}`,
                        borderRadius: "15px",
                      }}
                    >
                      <MainText size="body" color="black">
                        {item.content}
                      </MainText>
                      <MainText size="small" color="regular">
                        {DateTime.fromMillis(item.createdAtTimestamp).toFormat("dd/LL/y HH:mm")}
                      </MainText>
                    </Box>
                  </Box>
                ))
              : null}
          </Box>
        </Box>

        <MainSubmit onSubmit={handleSendMessage}>
          <TextMain
            name="Mensagem"
            value={textField}
            disabled={isMessageDisabled}
            adornmentDisabled={isMessageDisabled}
            onChange={setTextField}
            textColor={"regular"}
            adornmentIcon={isMessageDisabled ? <CancelScheduleSendRounded /> : <SendRounded />}
            adornmentClick={handleSendMessage}
            small={isMessageDisabled ? "Mensagens desabilitadas" : undefined}
          />
        </MainSubmit>
      </Box>
    </Box>
  );
}
